/**
 * Resource URLs, and the two questions asked about them.
 *
 * A connection is a pointer to a *server*; a grant narrows it to part of what
 * that server can reach. `clickhouse://analytics-prod/events` and
 * `github://acme/web` are the same shape — a scheme, then segments — so one
 * grammar covers every connector rather than each inventing its own.
 *
 * The grant is a pattern over that shape. `*` stands for exactly one segment,
 * `**` for zero or more remaining ones. A pattern shorter than the resource
 * does not match unless it ends in `**`, which makes "prod, and everything
 * under it" something someone has to write on purpose.
 *
 * This lives in `@reef/schema` rather than `@reef/connectors`, where it was
 * born, because a workflow document now writes these patterns too: a mount can
 * narrow what data it reaches, and the check that a narrowing really narrows
 * runs in `resolveStep`. Schema cannot import connectors — connectors imports
 * schema — and a second copy of a security grammar is how one of them ends up
 * failing open. So the grammar moved down and `@reef/connectors` re-exports it;
 * nothing that imported it from there had to change.
 */

export interface ResourceUrl {
  scheme: string;
  segments: string[];
  raw: string;
}

const SCHEME = /^([a-z][a-z0-9+.-]*):\/\/(.*)$/i;

export function parseResourceUrl(raw: string): ResourceUrl | null {
  const match = SCHEME.exec(raw.trim());
  if (!match) return null;
  const segments = match[2]!.split('/').filter((segment) => segment.length > 0);
  return { scheme: match[1]!.toLowerCase(), segments, raw: raw.trim() };
}

/**
 * Does one grant cover one resource?
 *
 * Both sides are parsed as resource URLs, so a malformed grant matches nothing
 * rather than everything — the failure mode of a typo in a scope should be
 * "denied", never "wide open".
 */
export function scopeMatches(grant: string, resource: string): boolean {
  const pattern = parseResourceUrl(grant);
  const target = parseResourceUrl(resource);
  if (!pattern || !target) return false;
  if (pattern.scheme !== '*' && pattern.scheme !== target.scheme) return false;
  return segmentsMatch(pattern.segments, target.segments);
}

function segmentsMatch(pattern: readonly string[], target: readonly string[]): boolean {
  let p = 0;
  let t = 0;

  while (p < pattern.length) {
    const segment = pattern[p]!;

    if (segment === '**') {
      // Greedy tail: `**` is only legal last, so everything left matches.
      return p === pattern.length - 1;
    }
    if (t >= target.length) return false;
    if (segment !== '*' && segment.toLowerCase() !== target[t]!.toLowerCase()) return false;

    p++;
    t++;
  }

  return t === target.length;
}

/**
 * Does one grant cover another *grant* — not a resource?
 *
 * `scopeMatches` answers "may this call happen"; this answers "is this
 * narrowing actually a narrowing". They are different questions, and using the
 * first for the second fails open in the direction that matters: a resource is
 * always literal, so `scopeMatches` never has to decide what a star on the
 * right-hand side means, and it reads one as a literal segment named `*`. Hand
 * it a pattern and a grant naming one database "matches" a pattern that names
 * every database, because the wildcard on the right was compared as text.
 *
 * So the rule is stated over patterns instead. A `**` in the outer pattern
 * covers whatever is left; a `*` covers exactly one segment whatever it is; a
 * literal covers only the same literal. Anything wildcarded on the inside that
 * is not wildcarded on the outside is wider, and is not covered.
 *
 * Conservative by construction: a pattern this cannot prove is narrower is
 * reported as not covered. The cost is a refusal somebody has to rewrite; the
 * alternative is a step quietly reaching past what its workflow granted.
 */
export function scopeCovers(outer: string, inner: string): boolean {
  const wide = parseResourceUrl(outer);
  const narrow = parseResourceUrl(inner);
  if (!wide || !narrow) return false;
  if (wide.scheme !== '*' && wide.scheme !== narrow.scheme) return false;
  return segmentsCover(wide.segments, narrow.segments);
}

function segmentsCover(outer: readonly string[], inner: readonly string[]): boolean {
  let o = 0;
  let i = 0;

  while (o < outer.length) {
    const wide = outer[o]!;
    // `**` is only legal last (`validateGrant`), so it covers everything left,
    // including an inner `**`.
    if (wide === '**') return o === outer.length - 1;
    if (i >= inner.length) return false;

    const narrow = inner[i]!;
    // An inner `**` stands for zero or more segments; nothing but an outer `**`
    // can be sure of covering it.
    if (narrow === '**') return false;
    if (wide !== '*' && wide.toLowerCase() !== narrow.toLowerCase()) return false;

    o++;
    i++;
  }

  return i === inner.length;
}

/** Reject a grant that cannot mean anything, at the point somebody types it. */
export function validateGrant(grant: string): string | null {
  const parsed = parseResourceUrl(grant);
  if (!parsed) return `"${grant}" is not a resource URL (expected scheme://segment/segment)`;
  const doubleStar = parsed.segments.indexOf('**');
  if (doubleStar !== -1 && doubleStar !== parsed.segments.length - 1) {
    return `"${grant}" puts ** somewhere other than the end, where it would match nothing`;
  }
  return null;
}
